import { Box, Divider } from '@mui/material';
import { SocketClientMessage } from '@okletsplay/core';
import { Route } from 'app/enums';
import { TournamentEntity } from 'app/interfaces/entity';
import { TournamentControls } from 'modules/tournaments/components/BracketTournament/TournamentControls';
import { TournamentSubheader } from 'modules/tournaments/components/BracketTournament/TournamentSubheader';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useFetchData } from 'app/hooks/store';
import { Identifier } from 'app/interfaces';
import { TournamentBreakAlert } from '../components/BracketTournament/TournamentBreakAlert/TournamentBreakAlert';
import { TournamentMatchGameJoinAlert } from '../components/BracketTournament/TournamentMatchGameJoinAlert';
import { TournamentSpectatorJoinAlert } from '../components/BracketTournament/TournamentSpectatorJoinAlert';
import { useTournamentDetails } from '../hooks';
import { getActiveTournamentMatchGame } from '../store/current-tournament-match-game/repository';
import { useStyles } from './TournamentLayout.styles';

const ROUTES_WITHOUT_CONTROLS = [Route.TournamentMyTeam, Route.TournamentAdmin];

export const TournamentLayout: FC = () => {
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const isMatchPath = ROUTES_WITHOUT_CONTROLS.some((path) => matchPath(path, pathname));

  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const { isCurrentUserPlayer } = useTournamentDetails();

  const actions = useMemo(
    () =>
      tournament?.id && isCurrentUserPlayer
        ? [getActiveTournamentMatchGame(tournament?.id as string)]
        : [],
    [tournament?.id, isCurrentUserPlayer]
  );
  const { fetch } = useFetchData<TournamentEntity>(actions, { fetchOnInit: true });

  const subscriptionHandler = useCallback(
    (identifier: Identifier) => {
      if (identifier !== tournament?.id) {
        return;
      }

      fetch(false);
    },
    [fetch, tournament?.id]
  );

  const subscriptions = useMemo(
    () => [
      {
        action: SocketClientMessage.TournamentMatchGame,
        handler: subscriptionHandler,
      },
    ],
    [subscriptionHandler]
  );

  useSocketSubscriber(subscriptions);

  return (
    <>
      <Divider />
      <TournamentSubheader tournament={tournament} />
      <Divider />

      <TournamentMatchGameJoinAlert />
      <TournamentBreakAlert />
      <TournamentSpectatorJoinAlert />

      {!isMatchPath && <TournamentControls tournament={tournament} />}
      <Box className={classes.root}>
        <Outlet />
      </Box>
    </>
  );
};
